/**
 * UpgradePrompt — shown when a free user hits the 10-subscription limit.
 *
 * Displays the limit reached message and a CTA to upgrade to Pro.
 * Calls the stripe-checkout edge function to create a Stripe Checkout session
 * and redirects the user to Stripe's hosted checkout page.
 */
import { Lock, Zap } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { FREE_PLAN_SUBSCRIPTION_LIMIT } from "@/hooks/useUserPlan";
import { supabase } from "@/integrations/supabase/supabaseClient";

interface UpgradePromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpgradePrompt({ open, onOpenChange }: UpgradePromptProps) {
  const { user } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [billingInterval, setBillingInterval] = useState<"month" | "year">(
    "year",
  );

  const handleUpgrade = async () => {
    if (!user) return;
    setIsRedirecting(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "stripe-checkout",
        {
          body: {
            billing_interval: billingInterval,
            success_url: `${window.location.origin}/app?upgrade=success`,
            cancel_url: `${window.location.origin}/app`,
          },
        },
      );

      if (error || !data?.url) {
        throw new Error(error?.message ?? "Failed to create checkout session");
      }

      window.location.href = data.url;
    } catch (err) {
      console.error("[UpgradePrompt] checkout error:", err);
      toast.error("Could not start checkout. Please try again.");
      setIsRedirecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <Lock size={24} className="text-primary" />
          </div>
          <DialogTitle className="text-xl">
            You&apos;ve reached the free plan limit
          </DialogTitle>
          <DialogDescription className="text-base">
            The free plan supports up to{" "}
            <strong>{FREE_PLAN_SUBSCRIPTION_LIMIT} active subscriptions</strong>
            . Upgrade to Pro for unlimited subscriptions, YNAB integration, data
            export, and more.
          </DialogDescription>
        </DialogHeader>

        {/* Billing interval toggle */}
        <div className="flex rounded-lg border border-border p-1">
          <button
            onClick={() => setBillingInterval("month")}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              billingInterval === "month"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Monthly · $3.99
          </button>
          <button
            onClick={() => setBillingInterval("year")}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              billingInterval === "year"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Annual · $29 <span className="text-xs opacity-75">save 40%</span>
          </button>
        </div>

        <div className="space-y-2 text-sm text-muted-foreground">
          {[
            "Unlimited subscriptions",
            "YNAB integration",
            "Data export (CSV / JSON)",
            "Full payment history",
            "Multi-currency FX rates",
          ].map((feature) => (
            <div key={feature} className="flex items-center gap-2">
              <Zap size={14} className="shrink-0 text-primary" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <Button
          onClick={handleUpgrade}
          disabled={isRedirecting}
          className="w-full"
          size="lg"
        >
          {isRedirecting
            ? "Redirecting to checkout…"
            : billingInterval === "year"
              ? "Upgrade to Pro — $29/year"
              : "Upgrade to Pro — $3.99/month"}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          Secure payment via Stripe. Cancel anytime.
        </p>
      </DialogContent>
    </Dialog>
  );
}
